// src/components/layout/DashboardLayout.js
import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Container,
  Alert,
  Link,
  Switch,
  FormControlLabel,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningIcon from "@mui/icons-material/Warning";
import { useEnvironment } from "../../context/EnvironmentContext";

const DashboardLayout = ({
  children,
  onLogout,
  title = "Fraud Detection Dashboard",
}) => {
  const { environment, changeEnvironment } = useEnvironment();


  // Handler for environment toggle
  const handleEnvironmentChange = (event) => {
    changeEnvironment(event.target.checked ? "staging" : "production");
  };

  const scrollToPanel = (panelId) => {
    const el = document.getElementById(panelId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Blue top bar with title and logout */}
      <AppBar position="static">
        <Toolbar sx={{ alignItems: "flex-end" }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Typography variant="h5" component="h1" sx={{ fontWeight: "bold", textAlign: "center", position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
            Easy Fraud Detection
          </Typography>
          <IconButton color="inherit" onClick={onLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
        <Typography sx={{ fontSize: "16px", color: "white", opacity: 0.9, textAlign: "center", pb: 1 }}>
          Get them before they get us.
        </Typography>
      </AppBar>

      {/* Secondary header with navigation */}
      <Box sx={{ backgroundColor: "#333", color: "white" }}>

        {/* Navigation links and staging warning */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "16px 75px 16px 75px",
            gap: 1,
          }}
        >
          {/* Centered navigation links */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Link
              component="button"
              onClick={() => scrollToPanel('panel-fraud-warnings')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              Fraud Warnings
            </Link>
            <Link
              component="button"
              onClick={() => scrollToPanel('panel-fraud-alerts')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              Fraud Matching Blocks
            </Link>
            <Link
              component="button"
              onClick={() => scrollToPanel('panel-chargeback-blocks')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              Chargeback Blocks
            </Link>
            <Link
              component="button"
              onClick={() => scrollToPanel('panel-card-declines')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              Multiple Attempts Blocks
            </Link>
            <Link
              component="button"
              onClick={() => scrollToPanel('panel-card-decline-history')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              Card Decline History
            </Link>
            <Link
              component="button"
              onClick={() => scrollToPanel('panel-international-orders')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              International Orders
            </Link>
            <Link
              component="button"
              onClick={() => scrollToPanel('paya-review-panel')}
              sx={{ color: '#93c5fd', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#60a5fa', textDecoration: 'underline' } }}
            >
              Paya Review
            </Link>
          </Box>

          {/* STAGING warning */}
          {environment === "staging" && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{
                backgroundColor: "#fef3c7",
                color: "#92400e",
                border: "1px solid #fbbf24",
                "& .MuiAlert-icon": {
                  color: "#f59e0b",
                },
                fontSize: "14px",
                padding: "8px 16px",
                mt: 1,
              }}
            >
              You are currently working in the STAGING environment. Changes will
              not affect production data.
            </Alert>
          )}
        </Box>
      </Box>

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, pt: 1, px: 3, pb: 3, backgroundColor: "#333" }}>
        <Container maxWidth="false">{children}</Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: "#333",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 75px",
        }}
      >
        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
          Fraud Detection Dashboard
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={environment === "staging"}
              onChange={handleEnvironmentChange}
              color="warning"
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#f59e0b",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#f59e0b",
                },
              }}
            />
          }
          label={environment === "staging" ? "STAGING" : "PRODUCTION"}
          sx={{
            "& .MuiFormControlLabel-label": {
              color: environment === "staging" ? "#f59e0b" : "white",
              fontWeight: "bold",
              fontSize: "13px",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default DashboardLayout;